import { findRoomByStandName } from '@bottlebooks/gatsby-plugin-california-pdf/src/templates/VieVinum/helpers/rooms';
import type { FilterDefinitions } from '@bottlebooks/gatsby-theme-event/src/components/Filters/useIndex';
import { defineMessage } from '@lingui/macro';
import { graphql } from 'gatsby';
import { rankings } from 'match-sorter';
import type { Vv_ExhibitorFiltersFragment } from './@types/exhibitorFilters.generated';

type Exhibitor = Vv_ExhibitorFiltersFragment['profile'] &
  Omit<Vv_ExhibitorFiltersFragment, '__typename'>;

export default function getExhibitorFilters(): FilterDefinitions<Exhibitor> {
  return {
    aggregations: {
      highlights: {
        title: defineMessage({ message: 'Highlights' }),
        // multiSelect: false, // TODO Implement
        size: 20,
        get: 'offeringTypes',
        translations: {
          videos: defineMessage({ message: 'Videos' }),
          onlineShop: defineMessage({ message: 'Online shop' }),
          imageGallery: defineMessage({ message: 'Image gallery' }),
        },
      },

      certifications: {
        title: defineMessage({ message: 'Certifications and memberships' }),
        conjunction: false,
        size: 20,
        get: ({ zertifizierungslogos }) => {
          if (
            zertifizierungslogos?.__typename !==
            'Bottlebooks_MultiSelectFieldValue'
          ) {
            return [];
          }
          return (zertifizierungslogos.values || [])
            .map(({ key }) => key)
            .filter(Boolean);
        },
        translations: {
          nachhaltigAustria: defineMessage({ message: 'Sustainable Austria' }),
          bio: defineMessage({ message: 'Organic' }),
          sektAustria: defineMessage({ message: 'Sekt Austria' }),
          respekt: defineMessage({ message: 'Respekt' }),
          demeter: defineMessage({ message: 'Demeter' }),
          mentee: defineMessage({
            message: 'Mentee Gerd A. Hoffmann Akademie',
          }),
        },
      },

      countryName: {
        title: defineMessage({ message: 'Countries' }),
        size: 100,
        conjunction: false,
        // Remove null values
        get: (exhibitor) => exhibitor.countryName || [],
      },

      origins: {
        title: defineMessage({ message: 'Origins' }),
        size: 100,
        conjunction: false,
        get: (exhibitor) =>
          exhibitor.region
            ?.trim()
            .split(/,\s*/)
            .flatMap((origin) => originHierarchy(origin)) || [],
        translations: {
          Austria: defineMessage({ message: 'Austria' }),
          Vienna: defineMessage({ message: 'Vienna' }),
          'Lower Austria': defineMessage({ message: 'Lower Austria' }),
          Styria: defineMessage({ message: 'Styria' }),
          'West Styria': defineMessage({ message: 'West Styria' }),
          Carinthia: defineMessage({ message: 'Carinthia' }),
          Germany: defineMessage({ message: 'Germany' }),
          Italy: defineMessage({ message: 'Italy' }),
          'South Tyrol': defineMessage({ message: 'South Tyrol' }),
          Sicily: defineMessage({ message: 'Sicily' }),
          Poland: defineMessage({ message: 'Poland' }),
          Slovenia: defineMessage({ message: 'Slovenia' }),
          Croatia: defineMessage({ message: 'Croatia' }),
          Greece: defineMessage({ message: 'Greece' }),
          Slavonia: defineMessage({ message: 'Slavonia' }),
        },
      },

      city: {
        title: defineMessage({ message: 'Locations' }),
        size: 300,
        conjunction: false,
        // Remove null values
        get: (exhibitor) => exhibitor.city || [],
      },

      associations: {
        title: defineMessage({ message: 'Associations' }),
        size: 300,
        conjunction: false,
        get: (exhibitor) => {
          return (
            exhibitor.associations
              ?.split('\n')
              .map((association) => association.trim())
              .filter(Boolean) || []
          );
        },
      },

      typicalVarieties: {
        title: defineMessage({ message: 'Typical varieties' }),
        size: 300,
        conjunction: true,
        multiSelect: true,
        get: (exhibitor) => exhibitor.profile?.typicalVarieties || [],
      },

      distributionInCountries: {
        title: defineMessage({ message: 'Distribution in countries' }),
        size: 200,
        conjunction: true,
        multiSelect: true,
        get: (exhibitor) =>
          exhibitor.profile?.distributionInCountries?.filter(Boolean),
      },

      seekingDistributionInCountries: {
        title: defineMessage({ message: 'Seeking distribution' }),
        size: 200,
        conjunction: true,
        multiSelect: true,
        get: (exhibitor) =>
          exhibitor.profile?.seekingDistributionInCountries?.filter(Boolean),
      },

      priceRanges: {
        title: defineMessage({ message: 'Price ranges' }),
        size: 100,
        conjunction: false,
        get: (exhibitor) =>
          exhibitor.priceRanges?.map(({ title }) => title).filter(Boolean) ||
          [],
      },
    },

    sortings: {
      byStandName: {
        title: defineMessage({ message: 'Table number' }),
        field: [sortBy.standName, 'profile.sortName', 'name'],
        order: 'asc',
        group: (exhibitor) => findRoomByStandName(exhibitor.stand?.name)?.name,
        groupHeader: 'room',
      },

      // byDataCompleteness: {
      //   title: defineMessage({ message: 'By data completeness' }),
      //   field: [
      //     sortBy.standName,
      //     sortBy.dataCompleteness,
      //     'profile.sortName',
      //     'name',
      //   ],
      //   order: 'asc',
      // },
      byExhibitorName: {
        title: defineMessage({ message: 'Exhibitor name' }),
        field: ['profile.sortName', 'name'],
        order: 'asc',
      },
    },
    search: {
      threshold: rankings.WORD_STARTS_WITH,
      keys: [
        { key: 'name', threshold: rankings.CONTAINS },
        (exhibitor) =>
          `${exhibitor.countryName || ''} ${exhibitor.region || ''} ${
            exhibitor.city || ''
          }`.trim(),
        (exhibitor) => exhibitor?.stand?.name?.replace(/[()]/g, '') || '',
      ],
    },
  };
}

// const completeness = {
//   ORGANIZER_REVIEWED: 1,
//   STEP_COMPLETED: 2,
//   STEP_PAYMENT: 2,
//   STEP_REVIEW: 3,
// };

function originHierarchy(origin) {
  return [
    originTranslations[origin] || origin,
    ...(originHierarchies[origin] || []),
  ].filter(Boolean);
}

const originTranslations = {
  Wien: 'Vienna',
  Niederösterreich: 'Lower Austria',
  Südtirol: 'South Tyrol',
  Kärnten: 'Carinthia',
  Weststeiermark: 'West Styria',
  Steiermark: 'Styria',
  Österreich: 'Austria',
  Griechenland: 'Greece',
  Sicilia: 'Sicily',
};

const originHierarchies = {
  Weinviertel: ['Lower Austria', 'Austria'],
  Neusiedlersee: ['Burgenland', 'Austria'],
  Wachau: ['Lower Austria', 'Austria'],
  Kremstal: ['Lower Austria', 'Austria'],
  Eisenberg: ['Burgenland', 'Austria'],
  Leithaberg: ['Burgenland', 'Austria'],
  Südsteiermark: ['Styria', 'Austria'],
  Kamptal: ['Lower Austria', 'Austria'],
  Wagram: ['Lower Austria', 'Austria'],
  Mittelburgenland: ['Burgenland', 'Austria'],
  Thermenregion: ['Lower Austria', 'Austria'],
  Carnuntum: ['Lower Austria', 'Austria'],
  'Vulkanland Steiermark': ['Styria', 'Austria'],
  Südtirol: ['Italy'],
  Pfalz: ['Germany'],
  Traisental: ['Lower Austria', 'Austria'],
  Mostviertel: ['Lower Austria', 'Austria'],
  Rust: ['Burgenland', 'Austria'],
  Weststeiermark: ['Styria', 'Austria'],
  Bergland: ['Austria'],
  Kärnten: ['Bergland', 'Austria'],
  Rheingau: ['Germany'],
  Baden: ['Germany'],
  'Baden-Württemberg': ['Germany'],
  Württemberg: ['Germany'],
  Rheinhessen: ['Germany'],
  Nahe: ['Germany'],
  Rosalia: ['Burgenland', 'Austria'],
  Tirol: ['Bergland', 'Austria'],
  Franken: ['Germany'],
  Sardinien: ['Italy'],
  Sicilia: ['Italy'],
  'Venetien - Lombardei': ['Italy'],
  'Slowenisches Istrien': ['Slovenia'],
  'Friaul Julisch Venetien': ['Italy'],
  Slavonia: ['Croatia'],
  Malopolska: ['Poland'],
};

// Sort helper functions
const sortBy = {
  standName: (exhibitor: Exhibitor) => {
    return exhibitor?.stand?.name
      .split(/[\s,-]+/)
      .map((segment) =>
        segment === 'Tisch'
          ? undefined
          : Number(segment)
          ? segment.padStart(3, '0')
          : segment
      )
      .filter(Boolean);
  },
  // dataCompleteness: (exhibitor) =>
  //   completeness[exhibitor?.registrationStatus] -
  //   (exhibitor?.registeredProducts?.totalCount > 0 ? 1 : 0),
};

export const fragment = graphql/* GraphQL */ `
  fragment vv_exhibitorFilters on Bottlebooks_SingleRegistration {
    companyId
    stand {
      name
    }
    zertifizierungslogos: customFieldValue(key: "zertifizierungslogos") {
      ... on Bottlebooks_MultiSelectFieldValue {
        values {
          key
        }
      }
    }
    registrationStatus
    registeredProducts {
      totalCount
    }
    profile {
      name
      sortName
      countryName: country(format: LOCALIZED)
      region
      city
      distributionInCountries
      seekingDistributionInCountries
      priceRanges {
        title
      }
      typicalVarieties
      sortName
      associations
    }
  }

  # offeringTypes isn't available on SingleRegistration
  fragment vv_exhibitorFilters_Exhibitor on Bottlebooks_Exhibitor {
    offeringTypes
  }
`;

//
